import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { ApiError, clearToken } from "../api";

/** Tenant login — for owner / manager / viewer of a farm tenant. Platform
 *  admins are bounced to /admin/login so the two audiences never share a
 *  form: reduces cred-stuffing surface and keeps the admin URL unadvertised
 *  in the normal user flow. */
export function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="login-shell">Đang tải…</div>;
  if (user) {
    return <Navigate to={user.is_platform_admin ? "/admin" : "/"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const me = await login(email, password);
      if (me.is_platform_admin) {
        // Admin đăng nhập nhầm trang tenant — invalidate token và bounce
        // sang trang admin. Tenant login không nên "nhận" admin session.
        clearToken();
        window.location.href = "/admin/login?redirected=admin";
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Đăng nhập thất bại");
      } else {
        setError("Không kết nối được tới cloud portal");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h2 style={{ margin: "0 0 4px 0" }}>🌿 Irrigation Cloud Portal</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Đăng nhập với tài khoản được owner/admin cấp.
        </p>

        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}

          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="field">
            <label>Mật khẩu</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="primary" disabled={submitting} style={{ width: "100%" }}>
            {submitting ? "Đang đăng nhập…" : "Đăng nhập"}
          </button>
        </form>

        <p className="muted" style={{ fontSize: "0.8rem", marginTop: 16, marginBottom: 0 }}>
          Chưa có tài khoản? Liên hệ owner của tenant.
        </p>
        <p className="muted" style={{ fontSize: "0.75rem", marginTop: 8, marginBottom: 0 }}>
          Là platform admin? <Link to="/admin/login">Đăng nhập tại đây</Link>
        </p>
      </div>
    </div>
  );
}
